import { useState, type FormEvent } from 'react';
import { getDeals, postDeal } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { DealSummary } from '../api/model';
import { Async, ErrorNotice, Notice } from '../components/Async';
import { CardHead } from '../components/Card';
import { Table, type Column } from '../components/Table';
import { Val, signClass } from '../components/Val';
import { Link } from '../lib/router';
import { formatUtc } from '../lib/time';

const columns: Column<DealSummary>[] = [
  { key: 'id', label: '#', numeric: true, sortValue: (d) => d.id, render: (d) => <span className="mono">#{d.id}</span> },
  { key: 'name', label: 'Ügylet', sortValue: (d) => d.name, render: (d) => <Link to={`/deals/${d.id}`}>{d.name}</Link> },
  { key: 'status', label: 'Állapot', sortValue: (d) => d.status, render: (d) => <span className={`pill${d.status === 'open' ? ' open' : ''}`}>{d.status === 'open' ? 'Nyitott' : 'Lezárt'}</span> },
  { key: 'ref', label: 'Ref. coin', sortValue: (d) => d.reference_asset, render: (d) => d.reference_asset ?? <span className="muted">—</span> },
  { key: 'loan', label: 'Hitel', help: 'loanSummary', ariaLabel: 'hitel', sortValue: (d) => d.loan_summary, render: (d) => <span className="mono">{d.loan_summary}</span> },
  { key: 'opened', label: 'Megnyitva (UTC)', sortValue: (d) => d.opened_at, render: (d) => <span className="mono">{formatUtc(d.opened_at)}</span> },
  { key: 'closed', label: 'Lezárva (UTC)', sortValue: (d) => d.closed_at, render: (d) => (d.closed_at ? <span className="mono">{formatUtc(d.closed_at)}</span> : <span className="muted">—</span>) },
  { key: 'outside', label: 'Kint, USD', numeric: true, help: 'outsideDeal', ariaLabel: 'kint lévő összeg', sortValue: (d) => d.outside_usd, render: (d) => <span className="mono"><Val value={d.outside_usd} reason={d.outside_reason} dp={0} /></span> },
  { key: 'interest', label: 'Kamat, USD', numeric: true, help: 'dealInterest', ariaLabel: 'kamat', sortValue: (d) => d.interest_usd, render: (d) => <span className="mono"><Val value={d.interest_usd} reason={d.interest_reason} dp={2} /></span> },
  { key: 'result', label: 'Eredmény, USD', numeric: true, help: 'result', ariaLabel: 'eredmény', sortValue: (d) => d.result_usd, render: (d) => <span className={`mono ${signClass(d.result_usd)}`}><Val value={d.result_usd} reason={d.result_reason} dp={2} sign /></span> },
  { key: 'tags', label: 'Címkék', render: (d) => d.tags.map((t) => <span key={t} className="pill" style={{ marginRight: 4 }}>{t}</span>) },
];

function NewDealForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [ref, setRef] = useState('BTC');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const [created, setCreated] = useState<{ id: number; name: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const d = await postDeal({ name: name.trim(), reference_asset: ref || null, tags: tags.split(',').map((t) => t.trim()).filter(Boolean), note: '' });
      setCreated({ id: d.id, name: d.name });
      setName('');
      setTags('');
      onCreated();
    } catch (x) {
      setErr(x instanceof Error ? x : new Error(String(x)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-label="Új ügylet">
      <CardHead title="Új ügylet" hint="Hitel nélkül is indítható; a kézi tételeket utána hozzárendelheted (Kézi bevitel)." />
      <form className="row-wrap" style={{ alignItems: 'flex-end' }} onSubmit={(e) => void submit(e)}>
        <div className="field" style={{ flex: '1 1 240px' }}>
          <label htmlFor="nd-name">Ügylet neve</label>
          <input id="nd-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="pl. BTC-short nov" />
        </div>
        <div className="field">
          <label htmlFor="nd-ref">Referencia-coin (stablecoin-hitelnél az árszinthez, D10)</label>
          <select id="nd-ref" className="input" value={ref} onChange={(e) => setRef(e.target.value)}>
            <option value="BTC">BTC</option>
            <option value="ETH">ETH</option>
            <option value="SOL">SOL</option>
            <option value="">Nincs</option>
          </select>
        </div>
        <div className="field" style={{ flex: '1 1 200px' }}>
          <label htmlFor="nd-tags">Címkék</label>
          <input id="nd-tags" className="input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="vesszővel elválasztva" />
        </div>
        <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
          Ügylet létrehozása
        </button>
      </form>
      {err ? <ErrorNotice error={err} /> : null}
      {created ? (
        <Notice kind="ok" title="Ügylet létrehozva">
          <Link to={`/deals/${created.id}`}>#{created.id} {created.name}</Link>
        </Notice>
      ) : null}
    </section>
  );
}

export function DealsPage() {
  const state = useApi(getDeals);
  return (
    <main className="page">
      <div className="page-head">
        <div className="titles">
          <h1>Ügyletek</h1>
          <div className="muted">Egy ügylet egy vagy több hitel és minden, ami belőlük származik (D26) · minden időpont UTC</div>
        </div>
        <Link className="btn primary" to="/mark">
          Binance-események megjelölése
        </Link>
      </div>
      <NewDealForm onCreated={state.reload} />
      <Async state={state}>
        {(deals) => (
          <section className="card tight" aria-label="Ügyletek">
            <CardHead title="Minden ügylet" />
            <Table columns={columns} rows={deals} rowKey={(d) => String(d.id)} defaultSort={{ key: 'opened', dir: 'desc' }} label="Ügyletek" empty="Még nincs ügylet. Hozz létre egyet, jelölj meg Binance-eseményeket, vagy vigy be kézi tételt." />
          </section>
        )}
      </Async>
    </main>
  );
}
